import MUIPopover, {
    PopoverClasses,
    PopoverOrigin,
    PopoverPosition,
    PopoverReference,
} from "@mui/material/Popover";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInsideClick, useOutsideClick } from "utils/utils";
import "./Popover.scss";

export interface PopoverProps {
    trigger?: ReactNode;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    closeOnInsideClick?: boolean;
    closeOnOutsideClick?: boolean;
    anchorReference?: PopoverReference;
    anchorPosition?: PopoverPosition;
    isOpen?: boolean;
    classes?: Partial<PopoverClasses>;
    toggleClassName?: string;
    contentClassName?: string;
    openOnAction?: "click" | "hover";
    rootClassName?: string;
    closeHandler?: () => void;
    onBlur?: (e) => void;
    actionOnOpen?: (event?: any) => void;
    handleScroll?: (e) => void;
    closeOnEnter?: boolean;
}

export const popoverPosition: Partial<PopoverProps> = {
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: {
        vertical: -5,
        horizontal: "left",
    },
};

export const centeredPopoverRootStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
};

export const Popover = ({
    children,
    anchorOrigin,
    transformOrigin,
    trigger,
    anchorPosition,
    isOpen,
    closeHandler,
    classes,
    toggleClassName,
    contentClassName,
    openOnAction = "click",
    closeOnInsideClick = true,
    closeOnOutsideClick = true,
    anchorReference = "anchorEl",
    rootClassName,
    onBlur,
    actionOnOpen,
    handleScroll,
    closeOnEnter,
}: React.PropsWithChildren<PopoverProps>) => {
    const popoverRef = useRef() as any;
    const anchorEl = useRef(null);
    const [internalIsOpened, setInternalIsOpened] = useState(false);

    const onMouseClick = useCallback(
        (event: any) => {
            if (openOnAction === "click") {
                setInternalIsOpened(true);
                actionOnOpen?.(event);
            }
        },
        [actionOnOpen, openOnAction]
    );

    const handleClose = () => {
        setInternalIsOpened(false);
    };

    const onMouseEnter = useCallback(
        (event: any) => {
            if (openOnAction === "hover") {
                setInternalIsOpened(true);
            }
        },
        [openOnAction]
    );

    const onMouseLeave = useCallback(() => {
        if (openOnAction === "hover") {
            setInternalIsOpened(false);
        }
    }, [openOnAction]);

    // A controlled popover (`isOpen` passed) can be closed by its parent, e.g. a
    // Cancel button inside the content, without going through handleClose. The
    // expanded class therefore follows the effective open state, so the trigger
    // does not keep looking "open" after the parent closes it.
    const isExpanded = isOpen ?? internalIsOpened;

    useEffect(() => {
        if (isOpen === false) {
            setInternalIsOpened(false);
        }
    }, [isOpen]);

    const toggleClasses = useMemo(
        () => `popover__toggle ${toggleClassName ?? ""} ${isExpanded ? "popover--expanded" : ""}`,
        [isExpanded, toggleClassName]
    );

    const triggerComponent = useMemo(
        () =>
            trigger ? (
                <span
                    ref={anchorEl}
                    className={toggleClasses}
                    onClick={onMouseClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    aria-haspopup="true"
                >
                    {trigger}
                </span>
            ) : null,
        [trigger, toggleClasses, onMouseClick, onMouseEnter, onMouseLeave]
    );

    useInsideClick(popoverRef, () => {
        if (closeOnInsideClick) {
            closeHandler?.();
            handleClose();
        }
    });

    useOutsideClick(popoverRef, () => {
        if (closeOnOutsideClick) {
            closeHandler?.();
        }
    });

    return (
        <>
            {triggerComponent}
            <MUIPopover
                anchorReference={anchorReference}
                anchorPosition={anchorPosition}
                anchorEl={anchorEl.current}
                onClose={(e) => {
                    handleClose();
                    onBlur?.(e);
                }}
                open={isOpen ?? internalIsOpened}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                classes={classes}
                disableRestoreFocus
                PaperProps={{ onMouseEnter, onMouseLeave }}
                className={rootClassName}
            >
                <div
                    className={contentClassName}
                    ref={popoverRef}
                    onScroll={handleScroll}
                    onKeyDown={(e) => {
                        if (closeOnEnter && e.key === "Enter") {
                            e.preventDefault();
                            handleClose();
                        }
                    }}
                >
                    {children}
                </div>
            </MUIPopover>
        </>
    );
};
